import { type ReactNode, type RefObject, useMemo } from 'react';
import { VideoPlayer } from '@/app/components/shared/VideoPlayer';
import { Loader } from '@/app/components/ui/loader';
import { useDevice } from '@/app/hooks';
import type { CommunityEnum, VideoPlayerErrorInfo } from '@/app/types';
import { cn } from '@/app/utils';
import { FeedDetailNativeVideoPlayer } from './FeedDetailNativeVideoPlayer';
import { getFeedDetailMediaSizingClassName } from './feed-detail-media-frame-utils';

export interface FeedDetailMediaFrameProps {
  src: string;
  poster?: string;
  aspectRatio: number | null;
  exploreTab: CommunityEnum;
  creationId: string;
  autoPlay?: boolean;
  hasAudio?: boolean;
  defaultVolume?: number;
  isError?: boolean;
  errorContent?: ReactNode;
  mediaAreaRef?: RefObject<HTMLDivElement | null>;
  onPlayEnd?: () => void;
  onError?: (errorInfo: VideoPlayerErrorInfo) => void;
}

export const FEED_DETAIL_H5_MEDIA_FRAME_CLASS = 'feed-detail-h5-media-frame';

export const FeedDetailMediaFrame = ({
  src,
  poster,
  aspectRatio,
  exploreTab,
  creationId,
  autoPlay = true,
  hasAudio,
  defaultVolume,
  isError = false,
  errorContent,
  mediaAreaRef,
  onPlayEnd,
  onError,
}: FeedDetailMediaFrameProps) => {
  const sniff = useDevice();
  const shouldUseNativeVideo = sniff.isMobile;

  const mediaSizingClassName = useMemo(
    () => getFeedDetailMediaSizingClassName({ aspectRatio, shouldUseNativeVideo }),
    [aspectRatio, shouldUseNativeVideo],
  );

  const mediaStyle = shouldUseNativeVideo || !aspectRatio ? undefined : { aspectRatio };
  const frameClassName = cn(
    'relative flex flex-1 items-center justify-center p-8 md:flex-none md:p-0',
    shouldUseNativeVideo && 'md:h-[56.25vw] md:max-h-[70vh] md:min-h-[240px] md:w-full md:flex-none md:p-0',
  );
  const innerClassName = cn('relative', mediaSizingClassName, shouldUseNativeVideo && FEED_DETAIL_H5_MEDIA_FRAME_CLASS);

  const fallback = isError ? (
    <div className="flex h-full w-full items-center justify-center text-sm text-system-white64">{errorContent}</div>
  ) : (
    <div className="flex h-full w-full items-center justify-center">
      <Loader />
    </div>
  );

  return (
    <div ref={mediaAreaRef} className={frameClassName}>
      <div className={innerClassName} style={mediaStyle}>
        {isError || !src || (!shouldUseNativeVideo && !aspectRatio) ? (
          fallback
        ) : shouldUseNativeVideo ? (
          <FeedDetailNativeVideoPlayer
            src={src}
            poster={poster}
            autoPlay={autoPlay}
            hasAudio={hasAudio}
            defaultVolume={defaultVolume}
            onPlayEnd={onPlayEnd}
            onError={onError}
            className="skip-login-check overflow-hidden rounded-12 md:rounded-none"
          />
        ) : (
          <VideoPlayer
            exploreTab={exploreTab}
            src={src}
            key={`creation_detail_${creationId}`}
            poster={poster}
            creationId={creationId}
            autoPlay={autoPlay}
            hasAudio={hasAudio}
            defaultVolume={defaultVolume}
            onPlayEnd={onPlayEnd}
            onError={onError}
            className="skip-login-check h-full max-h-full w-full max-w-full overflow-hidden rounded-12 object-contain md:rounded-none"
            enableSpaceShortcut
          />
        )}
      </div>
    </div>
  );
};
